import { PropsWithChildren, useEffect, useRef } from "react";
import {
  ApolloClient,
  ApolloProvider as ApolloClientProvider,
  from,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { setContext } from "@apollo/client/link/context";
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename";
import { useLoggedInAs } from "shared/hooks";
import { Owner } from "gql/generated";
import { LoadingPage } from "shared/components";
import { Grid } from "styled-system/jsx";

type ApolloProviderWithAuth0Props = PropsWithChildren;

export const ApolloProviderWithAuth0 = ({
  children,
}: ApolloProviderWithAuth0Props) => {
  const {
    isLoading,
    isAuthenticated,
    user,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();

  // Ensure the user is logged in
  useEffect(() => {
    if (!user && !isAuthenticated && !isLoading) {
      loginWithRedirect();
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, user]);

  // Determine who we're logged in as
  const loggedInAsRef = useRef<Owner | null>(null);
  loggedInAsRef.current = useLoggedInAs();

  // Set up the Apollo context (we need to use Refs to ensure we have the current variable value)
  const apolloLink = setContext(async (_, { headers, ...rest }) => {
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      },
    });

    if (!token) return { headers, ...rest };

    return {
      ...rest,
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
        // Determine who were're logged in as
        metadata: JSON.stringify(loggedInAsRef.current),
      },
    };
  });

  // Setup the Apollo client
  const httpLink = new HttpLink({
    uri: import.meta.env.VITE_API_SERVER_URL + "/graphql",
  });
  const removeTypenameLink = removeTypenameFromVariables();
  const link = from([apolloLink, removeTypenameLink, httpLink]);
  const apolloClient = useRef<ApolloClient<NormalizedCacheObject> | null>(null);

  if (!apolloClient.current) {
    apolloClient.current = new ApolloClient({
      link,
      cache: new InMemoryCache(),
      defaultOptions: {
        query: {
          fetchPolicy: "no-cache",
        },
        mutate: {
          fetchPolicy: "no-cache",
        },
        watchQuery: {
          fetchPolicy: "no-cache",
        },
      },
    });
  }

  if (!user) {
    return (
      <Grid height="svh" width="full" backgroundColor={"bg.wa.lightest"}>
        <LoadingPage />
      </Grid>
    );
  }

  return (
    <ApolloClientProvider client={apolloClient.current}>
      {children}
    </ApolloClientProvider>
  );
};
